// Click-to-zoom-then-hover-to-pan for the main product image.
//
// Interaction model: a real mouse click "activates" zoom mode on the
// image; while active, moving the mouse pans the zoomed overlay around;
// clicking again — and ONLY clicking again — deactivates it. Moving the
// mouse off the image does NOT close it; it's click to start, click to
// end, full stop. This intentionally does NOT activate on plain hover —
// an earlier version did that, and it flickered violently: the zoom
// overlay is positioned directly on top of the image, so as soon as it
// appeared the cursor was now over the *overlay* instead of the image,
// which fired `mouseleave` on the image, which removed the overlay,
// which put the cursor back over the image, which fired `mouseenter`
// again — an infinite create/destroy loop, many times a second.
//
// Two things fix that here:
//   1. Activation only happens on an explicit click, not on hover, so
//      there's no enter/leave cycle to begin with.
//   2. There is no mouseleave handling at all anymore, by design — see
//      below for the click-to-start/click-to-end model.
//
// Mouse vs. touch is distinguished via PointerEvent.pointerType, which
// tells us the actual input mechanism directly rather than guessing from
// screen width — a touch tap can never activate this, on any device,
// regardless of viewport size. The width check below is kept as a second,
// independent layer on top of that.
(function () {
  'use strict';

  function createOverlay(image) {
    var overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', image.src);

    var overlay = document.createElement('div');
    prepareOverlay(overlay, overlayImage);
    // Starts invisible; faded in below once actually in the DOM, so the
    // transition has something to animate from instead of just appearing.
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';

    image.style.transition = 'opacity 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)';
    image.style.opacity = '0.5';
    toggleLoadingSpinner(image);

    overlayImage.onload = function () {
      toggleLoadingSpinner(image);
      image.parentElement.insertBefore(overlay, image);
      image.style.opacity = '1';
      // Force a style recalculation before changing opacity again — the
      // browser can otherwise coalesce "insert at opacity:0" and "set
      // opacity:1" into a single paint if they happen in the same tick,
      // which skips the transition entirely and looks like an instant pop.
      void overlay.offsetWidth;
      overlay.style.opacity = '1';
    };

    return overlay;
  }

  function prepareOverlay(container, image) {
    container.setAttribute('class', 'image-magnify-full-size');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = 'url(' + image.src + ')';
    container.style.backgroundColor = 'var(--gradient-background)';
    // Critical: without this, the overlay (which sits visually on top of
    // the image) intercepts every subsequent click itself — so a second
    // click meant to close the zoom was landing on the overlay, not the
    // image, and never reaching the toggle logic at all. That's why it
    // never exited. pointer-events:none makes the overlay invisible to
    // clicks entirely; they pass straight through to the image beneath.
    container.style.pointerEvents = 'none';
  }

  function toggleLoadingSpinner(image) {
    var loadingSpinner = image
      .closest('.product__media-item')
      ?.parentElement
      ?.querySelector('.loading__spinner');

    if (loadingSpinner) {
      loadingSpinner.classList.toggle('hidden');
    }
  }

  function moveWithHover(image, overlay, event, zoomRatio) {
    var ratio = image.height / image.width;
    var container = image.getBoundingClientRect();
    var xPosition = event.clientX - container.left;
    var yPosition = event.clientY - container.top;
    var xPercent = (xPosition / (image.clientWidth / 100)) + '%';
    var yPercent = (yPosition / ((image.clientWidth * ratio) / 100)) + '%';

    overlay.style.backgroundPosition = xPercent + ' ' + yPercent;
    overlay.style.backgroundSize = (image.width * zoomRatio) + 'px';
  }

  // Tracks whichever single image is currently zoomed, across the whole
  // gallery — activating a new one closes the previous one automatically,
  // so multiple images can never end up stuck zoomed at the same time.
  var currentlyActiveDeactivate = null;

  function attachClickThenHoverZoom(image, zoomRatio) {
    var parent = image.parentElement;
    var overlay = null;
    var rafId = null;
    var active = false;

    function activate(event) {
      if (active) return;
      if (currentlyActiveDeactivate && currentlyActiveDeactivate !== deactivate) {
        currentlyActiveDeactivate();
      }
      active = true;
      currentlyActiveDeactivate = deactivate;
      overlay = createOverlay(image);
      moveWithHover(image, overlay, event, zoomRatio);
    }

    function deactivate() {
      if (!active) return;
      active = false;
      if (currentlyActiveDeactivate === deactivate) {
        currentlyActiveDeactivate = null;
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (overlay) {
        var overlayToRemove = overlay;
        overlayToRemove.style.opacity = '0';
        setTimeout(function () {
          overlayToRemove.remove();
        }, 300); // matches the 0.3s fade above — removes only after it's finished
        overlay = null;
      }
      image.style.opacity = '';
    }

    // pointerup (not click) so we can check pointerType and be certain
    // this only ever fires for an actual mouse — never a touch tap, even
    // on a touch-capable desktop/hybrid device.
    image.addEventListener('pointerup', function (event) {
      if (event.pointerType !== 'mouse') return;
      if (active) {
        deactivate();
      } else {
        activate(event);
      }
    });

    // Tracked on the PARENT, not the image — the overlay is a sibling of
    // the image inside this same parent, so panning across either one
    // keeps mousemove firing on a consistent element the whole time.
    parent.addEventListener('mousemove', function (event) {
      if (!active || !overlay) return;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        moveWithHover(image, overlay, event, zoomRatio);
      });
    });

    // No mouseleave handler here on purpose — moving the mouse off the
    // image no longer closes the zoom. Click to start, click to end is
    // the only way in or out now; the overlay just stays put wherever
    // you last moved it until you click again.
  }

  function enableZoomOnHover(zoomRatio) {
    // Plain width check instead of matchMedia('(hover: hover) and
    // (pointer: fine)'). That media-feature check is technically more
    // "correct" in the abstract, but browser dev tools' responsive/device
    // emulation mode commonly forces hover:none and pointer:coarse to
    // simulate touch — regardless of the width chosen, and regardless of
    // whether it's being used to represent "desktop" in a resized window.
    // A plain width check matches the same 750px breakpoint used
    // everywhere else in this theme, and behaves predictably under
    // dev-tools emulation instead of silently going dark during testing.
    if (window.innerWidth < 750) return;

    var images = document.querySelectorAll('.image-magnify-hover');
    images.forEach(function (image) {
      attachClickThenHoverZoom(image, zoomRatio);
    });
  }

  // Reads a per-section zoom level if the theme set one (see
  // main-product.liquid), otherwise falls back to the previous default.
  var zoomRatio = (typeof window.themeZoomRatio === 'number' && window.themeZoomRatio > 0)
    ? window.themeZoomRatio
    : 2;

  enableZoomOnHover(zoomRatio);
})();